if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();

      if (this.hasChildNodes()) {
        this.init();
      }
    }

    init() {
      this.form = this.querySelector('.js-product-form');

      if (!this.form) return;

      const variantInput = this.form.querySelector('[name="id"]');

      if (variantInput) {
        variantInput.disabled = false;
      }

      this.cartDrawer = document.querySelector('cart-drawer');
      this.submitBtn = this.querySelector('[name="add"]');

      if (
        this.submitBtn &&
        theme.settings.afterAtc !== 'no-js'
      ) {
        this.form.addEventListener(
          'submit',
          this.handleSubmit.bind(this)
        );
      }
    }

    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (
        !this.submitBtn ||
        this.submitBtn.getAttribute('aria-disabled') === 'true'
      ) {
        return;
      }

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      const originalButtonText =
        this.submitBtn.dataset.addToCartText || 'Add to cart';

      let addSucceeded = false;

      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.remove('is-success');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);

      const requestedQuantity = Math.max(
        1,
        Number(formData.get('quantity')) || 1
      );

      const variantId = Number(formData.get('id'));
      const sampleVariantIds = String(
        this.dataset.wwfsSampleVariantIds || ''
      ).split(',').filter(Boolean).map(Number);

      if (sampleVariantIds.includes(variantId)) {
        try {
          const sampleCartResponse = await fetch(`${theme.routes.cart}.js`);

          if (!sampleCartResponse.ok) {
            throw new Error('Unable to check the current sample quantity.');
          }

          const sampleCart = await sampleCartResponse.json();
          const sampleCount = sampleCart.items.reduce((total, item) => {
            const title = String(item.variant_title || '').toLowerCase();
            const sku = String(item.sku || '').toLowerCase();
            return total + (
              title.includes('sample') || sku.includes('sample')
                ? Number(item.quantity || 0)
                : 0
            );
          }, 0);

          if (sampleCount + requestedQuantity > 3) {
            this.setErrorMsgState(
              'You can order a maximum of three free flooring samples per order.'
            );
            this.submitBtn.removeAttribute('aria-disabled');
            this.submitBtn.classList.remove('is-loading');
            this.submitBtn.textContent = originalButtonText;
            return;
          }
        } catch (sampleError) {
          this.setErrorMsgState(
            'We could not confirm your current sample quantity. Please refresh the page and try again.'
          );
          this.submitBtn.removeAttribute('aria-disabled');
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.textContent = originalButtonText;
          return;
        }
      }

      let sections = 'cart-icon-bubble';

      if (this.cartDrawer) {
        const cartDrawerSection =
          this.cartDrawer.closest('.shopify-section');

        if (cartDrawerSection) {
          sections += `,${cartDrawerSection.id.replace(
            'shopify-section-',
            ''
          )}`;
        }
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        /*
         * Add the product immediately. A cart-reading request must never
         * delay or prevent the actual add-to-cart request.
         */
        const response = await fetch(
          theme.routes.cartAdd,
          fetchRequestOpts
        );

        const data = await response.json();

        let error =
          typeof data.description === 'string'
            ? data.description
            : data.message;

        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => {
            const messages = Array.isArray(item[1])
              ? item[1]
              : [item[1]];

            return messages.join(', ');
          });
        }

        if (data.status || !response.ok) {
          this.setErrorMsgState(
            error || 'Sorry, this product could not be added to your cart.'
          );

          throw new Error(`Unable to add product: ${response.status}`);
        }

        /*
         * Shopify has confirmed that the product was added.
         * Stop the spinner before displaying the success state.
         */
        addSucceeded = true;

        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.classList.add('is-success');
        this.submitBtn.textContent = '✓ Added to Cart';

        if (theme.settings.afterAtc === 'page') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          ProductForm.updateCartIcon(data);

          const quickAddDrawer = this.closest('quick-add-drawer');

          if (quickAddDrawer) {
            quickAddDrawer.addedToCart();
          }

          setTimeout(() => {
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer &&
                  theme.settings.afterAtc === 'drawer'
              );
            } else if (
              window.location.pathname === theme.routes.cart
            ) {
              const cartItems = document.querySelector('cart-items');

              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        /*
         * Retrieve the completed cart for the theme's cart events. This is
         * deliberately isolated: if the secondary refresh fails, Shopify
         * has still added the item and the customer must not see an error.
         */
        try {
          const newCartResponse = await fetch(
            `${theme.routes.cart}.js`
          );

          if (!newCartResponse.ok) {
            throw new Error(
              `Unable to refresh cart: ${newCartResponse.status}`
            );
          }

          const newCartData = await newCartResponse.json();

          /*
           * Match Shopify's returned line by key where possible. This also
           * distinguishes lines with different properties or selling plans.
           */
          const updatedCartItem =
            newCartData.items.find(
              (item) => data.key && item.key === data.key
            ) ||
            newCartData.items.find(
              (item) => item.variant_id === data.variant_id
            );

          const newQuantity = updatedCartItem
            ? updatedCartItem.quantity
            : requestedQuantity;

          const oldQuantity = Math.max(
            0,
            newQuantity - requestedQuantity
          );

          if (oldQuantity > 0) {
            this.dispatchEvent(
              new CustomEvent('on:line-item:change', {
                bubbles: true,
                detail: {
                  cart: newCartData,
                  variantId: data.variant_id,
                  oldQuantity,
                  newQuantity
                }
              })
            );
          } else {
            this.dispatchEvent(
              new CustomEvent('on:cart:add', {
                bubbles: true,
                detail: {
                  cart: newCartData,
                  variantId: data.variant_id
                }
              })
            );
          }
        } catch (refreshError) {
          console.warn(
            'Product added, but the follow-up cart refresh failed.',
            refreshError
          );

          /*
           * The add request already succeeded. Send a confirmed success
           * event using the add response so collection buttons do not remain
           * stuck on "Adding..." when only the secondary refresh fails.
           */
          this.dispatchEvent(
            new CustomEvent('on:cart:add', {
              bubbles: true,
              detail: {
                cart: data,
                variantId: data.variant_id
              }
            })
          );
        }
      } catch (error) {
        console.log(error);

        const errorText =
          this.errorMsg && !this.errorMsg.hidden
            ? this.errorMsg.textContent.trim()
            : error.message;

        this.dispatchEvent(
          new CustomEvent('on:cart:error', {
            bubbles: true,
            detail: {
              error: errorText
            }
          })
        );
      } finally {
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.classList.remove('is-loading');

        if (addSucceeded) {
          setTimeout(() => {
            this.submitBtn.classList.remove('is-success');
            this.submitBtn.textContent = originalButtonText;
          }, 1400);
        } else {
          this.submitBtn.classList.remove('is-success');
          this.submitBtn.textContent = originalButtonText;
        }
      }
    }

    static updateCartIcon(response) {
      const cartIconBubble =
        document.getElementById('cart-icon-bubble');

      if (
        cartIconBubble &&
        response.sections &&
        response.sections['cart-icon-bubble']
      ) {
        cartIconBubble.innerHTML =
          response.sections['cart-icon-bubble'];
      }
    }

    setErrorMsgState(error = false) {
      this.errorMsg =
        this.errorMsg || this.querySelector('.js-form-error');

      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;

      if (error) {
        this.errorMsg.innerHTML = '';

        const errorArray = Array.isArray(error)
          ? error
          : [error];

        errorArray.forEach((err, index) => {
          if (index > 0) {
            this.errorMsg.insertAdjacentHTML(
              'beforeend',
              '<br>'
            );
          }

          this.errorMsg.insertAdjacentText(
            'beforeend',
            String(err)
          );
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
